import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, Users, Baby, UserPlus, Heart, Cake, Bot, MessageCircle, Music2, CalendarCheck2, BarChart3 } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";
import { applyViewAs, getViewAsRole, getSimulatedIsMusician } from "./roleSimulator";

// Mesma lógica de isLegacyFamiliaHost (index.js): no domínio "familia." o
// Família Mosaico vive na raiz; nos demais, sob /familia.
const isLegacyFamiliaHost =
  typeof window !== "undefined" &&
  (window.location.hostname.startsWith("familia.") || window.location.hostname === "localhost");
const HOME_PATH = isLegacyFamiliaHost ? "/" : "/familia";

// Catálogo de seções administrativas -- pra adicionar uma nova área no
// painel (ex.: relatórios, integrações) basta acrescentar uma entrada aqui,
// com os papéis que enxergam o card. ADMIN sempre vê tudo (ver
// requireSessionPreHandler em lib/session.js, no backend).
const ADMIN_SECTIONS = [
  {
    key: "visitantes",
    title: "Visitantes",
    description: "Quem visitou, por etapa.",
    to: "/admin/visitantes",
    icon: UserPlus,
    // Liberado tambem pra SECRETARIA e ACOLHIMENTO (pedido: "dar acesso de
    // visitantes para perfil secretaria, e para perfil acolhimento") --
    // mesmo gate aplicado no backend (requireVisitorAccess em conecta.js)
    // e na propria pagina (AdminVisitantes.jsx). RECEPCAO tambem entra aqui,
    // mas com acesso bem mais restrito dentro da propria pagina (so
    // "Novo visitante" -- requireVisitorReceptionAccess em conecta.js).
    roles: ["ADMIN", "SECRETARIA", "ACOLHIMENTO", "RECEPCAO"],
    group: "cadastros",
    badgeKey: "visitantesNovos",
    badgeLabel: (n) => `${n} novo${n === 1 ? "" : "s"}`
  },
  {
    key: "acolhimento",
    // Nome do card alinhado ao titulo que a propria pagina ja usa (ver
    // AdminAcolhimento.jsx) -- "Painel de acolhimento" ficou descolado do
    // que a tela mostra e confundia com o acompanhamento de visitantes
    // (card "Visitantes" acima).
    title: "Painel de cuidado",
    description: "Engajamento digital e pendências pastorais.",
    to: "/admin/acolhimento",
    icon: Heart,
    roles: ["ADMIN"],
    group: "cuidado",
    badgeKey: "painelCuidadoPendencias",
    badgeLabel: (n) => `${n} pendente${n === 1 ? "" : "s"}`
  },
  {
    key: "agente",
    title: "Agente WhatsApp",
    description: "Contatos do Barnabé e histórico de envios.",
    to: "/admin/agente",
    icon: MessageCircle,
    roles: ["ADMIN"],
    group: "cuidado",
    badgeKey: "agenteContatosComProblema",
    badgeLabel: (n) => `${n} com problema`
  },
  {
    key: "agente-relatorio",
    title: "Agente Barnabé -- curadoria",
    description: "Perguntas e temas das conversas -- site e WhatsApp.",
    to: "/admin/agente-relatorio",
    icon: Bot,
    roles: ["ADMIN"],
    group: "cuidado",
    badgeKey: null
  },
  {
    key: "membros",
    title: "Membros",
    description: "Cadastro de membresia da igreja.",
    to: "/admin/membros",
    icon: Users,
    // Quem tem so o papel de consulta (SECRETARIA_CONSULTA) ve uma versao
    // reduzida (LGPD) dentro da propria tela, sem precisar de um card
    // separado aqui.
    roles: ["ADMIN", "SECRETARIA", "SECRETARIA_CONSULTA"],
    group: "cadastros",
    // Mesmo conceito de "PENDENTE" ja usado no filtro/banner de
    // AdminMembros.jsx (cadastro aguardando aprovacao da secretaria) --
    // pedido do Rodrigo pra ver direto no card, sem precisar entrar
    // (18/10/2026).
    badgeKey: "membrosPendencias",
    badgeLabel: (n) => `${n} pendente${n === 1 ? "" : "s"}`
  },
  {
    key: "criancas",
    title: "Kids e Teens",
    description: "Cadastro do ministério infantil.",
    to: "/admin/criancas",
    icon: Baby,
    // Mesmo raciocinio de SECRETARIA_CONSULTA acima.
    roles: ["ADMIN", "SECRETARIA", "SECRETARIA_CONSULTA"],
    group: "cadastros",
    badgeKey: null
  },
  {
    key: "aniversariantes",
    title: "Aniversariantes",
    description: "Quem faz aniversário hoje.",
    to: "/admin/aniversariantes",
    icon: Cake,
    roles: ["ADMIN", "SECRETARIA", "SECRETARIA_CONSULTA"],
    group: "cadastros",
    badgeKey: null
  },
  {
    key: "repertorio",
    title: "Repertório",
    description: "Catálogo de músicas pra equipe de louvor estudar antes do ensaio.",
    to: "/admin/repertorio",
    icon: Music2,
    roles: ["ADMIN", "MUSICO"],
    group: "ministerios",
    badgeKey: null
  },
  {
    key: "escalas",
    title: "Escalas",
    description: "Louvor, Zeladoria, Kids, Teens, Mídia, Café e Recepção -- quem está escalado em cada culto.",
    to: "/admin/escalas",
    icon: CalendarCheck2,
    // Mesmo gate do Repertorio -- ADMIN e MUSICO podem montar/editar a
    // escala (pedido explicito do Drigo, 17/10/2026).
    roles: ["ADMIN", "MUSICO"],
    group: "ministerios",
    badgeKey: null
  },
  {
    key: "usuarios",
    title: "Usuários",
    description: "Papéis e acesso de cada pessoa.",
    to: "/admin/usuarios",
    icon: ShieldCheck,
    roles: ["ADMIN"],
    group: "sistema",
    badgeKey: null
  },
  {
    key: "analytics",
    title: "Analytics do site",
    description: "Volume de acessos -- páginas, e-books e devocionais.",
    to: "/admin/analytics",
    icon: BarChart3,
    // Pedido do Rodrigo (21/10/2026): "pode trazer dados do analytics
    // para uma pagina no administrativo?". Dados vêm do Google Analytics
    // (GA4) via mosaico-api/src/lib/ga4.js.
    roles: ["ADMIN"],
    group: "sistema",
    badgeKey: null
  }
];

// Pedido do Rodrigo depois da revisao de UX da home: agrupar por frequencia
// de uso em vez de ordem arbitraria -- "Cuidado e comunicacao" primeiro
// (uso diario), "Cadastros" depois, "Sistema" (config, uso raro) por ultimo.
const SECTION_GROUPS = [
  { key: "cuidado", label: "Cuidado e comunicação" },
  { key: "cadastros", label: "Cadastros" },
  { key: "ministerios", label: "Ministérios" },
  { key: "sistema", label: "Sistema" }
];

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardLinkStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 20,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)",
  textDecoration: "none",
  color: "inherit"
};

const cardIconWrapStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 12,
  flexShrink: 0,
  border: "1px solid rgba(125,211,252,0.38)",
  background: "rgba(14,165,233,0.14)",
  color: "#BAE6FD"
};

const groupLabelStyle = {
  margin: "26px 0 10px",
  fontSize: 12,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "#8089a0",
  fontWeight: 900
};

// So aparece quando o numero e > 0 -- card sem pendencia fica limpo, sem
// badge nenhuma (ver revisao de UX: "nao clutterar com zero-state").
const badgePillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginBottom: 5,
  padding: "3px 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 900,
  color: "#F5D28B",
  background: "rgba(230,177,90,0.14)",
  border: "1px solid rgba(230,177,90,0.4)",
  width: "fit-content"
};

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={HOME_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function AdminHome() {
  const [status, setStatus] = useState("checking");
  const [realRoles, setRealRoles] = useState([]);
  // "Ver como" -- so aparece pra quem tem ADMIN nas roles reais (ver
  // isRealAdmin abaixo). Guardado no localStorage via roleSimulator, entao
  // sobrevive a navegacao entre paginas administrativas.
  const [viewAsRole] = useState(getViewAsRole());
  // Numeros curtos pra badge nos 3 cards de maior uso (ver ADMIN_SECTIONS,
  // campo badgeKey) -- so some, sem dado pessoal. Falha fica em silencio: o
  // card so some sem o badge, a navegacao continua funcionando normal.
  const [resumo, setResumo] = useState(null);
  // Capacidade de musico mora na Person (isMusician), nao em User.roles --
  // mesmo motivo/correcao de AdminEscalas.jsx e AdminRepertorio.jsx
  // (20/10/2026, caso do Gabriel: ficava sem acesso mesmo escalado, porque
  // um merge de cadastro anterior nunca sincronizou User.roles). Guardado
  // a parte (nao dentro de realRoles) porque nao e' uma role de banco de
  // verdade -- so' vira um "MUSICO" sintetico em effectiveRoles mais abaixo,
  // via getSimulatedIsMusician (que ja respeita o "ver como").
  const [personIsMusician, setPersonIsMusician] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const myRoles = Array.isArray(me?.roles) ? me.roles : [];
        const myIsMusicianByPerson = Boolean(me?.person?.isMusician);
        const hasAnyAdminSection = ADMIN_SECTIONS.some(
          (section) => myRoles.includes("ADMIN") || section.roles.some((role) => myRoles.includes(role) || (role === "MUSICO" && myIsMusicianByPerson))
        );
        if (hasAnyAdminSection && !me.passwordMustChange) {
          setRealRoles(myRoles);
          setPersonIsMusician(myIsMusicianByPerson);
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    let alive = true;
    apiFetch("/admin/home-resumo", { method: "GET", skipSessionExpiredHandler: true })
      .then((data) => {
        if (alive) setResumo(data);
      })
      .catch(() => {
        // Papel sem acesso a nenhum card com badge (ex.: so SECRETARIA_CONSULTA)
        // cai aqui com 403 -- sem problema, os cards sem badgeKey continuam normais.
      });
    return () => {
      alive = false;
    };
  }, [status]);

  const isRealAdmin = realRoles.includes("ADMIN");
  const baseEffectiveRoles = applyViewAs(realRoles, viewAsRole);
  const isMusicianByPerson = getSimulatedIsMusician(isRealAdmin, personIsMusician);
  const effectiveRoles = isMusicianByPerson && !baseEffectiveRoles.includes("MUSICO")
    ? [...baseEffectiveRoles, "MUSICO"]
    : baseEffectiveRoles;

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à equipe administrativa. Entre com uma conta autorizada para ver o painel.</CenteredMessage>;
  }

  const visibleSections = ADMIN_SECTIONS.filter(
    (section) => effectiveRoles.includes("ADMIN") || section.roles.some((role) => effectiveRoles.includes(role))
  );

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <Link to={HOME_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
          Área administrativa
        </p>
        <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 950, color: "#FFF" }}>Painel administrativo</h1>
        <p style={{ margin: "0 0 26px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 620 }}>
          Acesso às áreas de gestão do app. Conforme novas funcionalidades forem entrando, os cards aparecem aqui.
        </p>

        {SECTION_GROUPS.map((group) => {
          const sections = visibleSections.filter((section) => section.group === group.key);
          if (!sections.length) return null;
          return (
            <div key={group.key}>
              <p style={groupLabelStyle}>{group.label}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
                {sections.map((section) => {
                  const Icon = section.icon;
                  const count = section.badgeKey ? resumo?.[section.badgeKey] : null;
                  return (
                    <Link key={section.key} to={section.to} style={cardLinkStyle}>
                      <div style={cardIconWrapStyle}>
                        <Icon size={20} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 900, color: "#FFF" }}>{section.title}</p>
                        {Boolean(count) && <p style={badgePillStyle}>{section.badgeLabel(count)}</p>}
                        <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc", lineHeight: 1.5 }}>{section.description}</p>
                      </div>
                      <ArrowRight size={16} style={{ flexShrink: 0, marginTop: 2, color: "#7DD3FC" }} />
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
